import React from 'react';
import styled from 'styled-components';
import { Container } from '../ui';
import { LocationHighlight, LocationKind } from '../../types/location';
import { kindLabel, toParagraphs } from './locationModel';
import { Band, Eyebrow, SandBand, SectionHeading, goldText, ink } from './tokens';

const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 4fr) minmax(0, 7fr);
  gap: clamp(2rem, 5vw, 5rem);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const Aside = styled.div`
  @media (min-width: 861px) {
    position: sticky;
    top: 7rem;
    align-self: start;
  }
`;

const Prose = styled.div`
  color: ${({ theme }) => theme.colors.neutral[700]};
  font-size: 1.06rem;
  line-height: 1.85;

  p + p { margin-top: 1.15rem; }

  p:first-child {
    font-size: clamp(1.15rem, 1.8vw, 1.32rem);
    line-height: 1.7;
    color: ${({ theme }) => ink(theme)};
  }

  p { white-space: pre-line; }
`;

const Highlights = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 2.25rem clamp(1.5rem, 3vw, 3rem);
  margin-top: 2.75rem;
`;

const Highlight = styled.li`
  padding-top: 1.25rem;
  border-top: 1px solid ${({ theme }) => goldText(theme)};

  h3 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.15rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${({ theme }) => ink(theme)};
  }

  p {
    margin-top: 0.6rem;
    color: ${({ theme }) => theme.colors.neutral[600]};
    line-height: 1.7;
  }
`;

const HighlightsHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const ofKind = (kind: LocationKind) => `Sobre o ${kindLabel(kind).toLowerCase()}`;

export const LocationIntro: React.FC<{ name: string; kind: LocationKind; intro?: string | null }> = ({
  name, kind, intro,
}) => {
  const paragraphs = toParagraphs(intro);
  if (paragraphs.length === 0) return null;
  return (
    <Band aria-labelledby="local-sobre">
      <Container>
        <Split>
          <Aside>
            <Eyebrow>{ofKind(kind)}</Eyebrow>
            <SectionHeading id="local-sobre">{name}</SectionHeading>
          </Aside>
          <Prose>
            {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          </Prose>
        </Split>
      </Container>
    </Band>
  );
};

export const LocationHighlights: React.FC<{
  name: string;
  kind: LocationKind;
  items: LocationHighlight[];
}> = ({ name, kind, items }) => {
  if (items.length === 0) return null;
  return (
    <SandBand aria-labelledby="local-destaques">
      <Container>
        <HighlightsHeader>
          <Eyebrow>{name}</Eyebrow>
          <SectionHeading id="local-destaques">
            Destaques do {kindLabel(kind).toLowerCase()}
          </SectionHeading>
        </HighlightsHeader>
        <Highlights>
          {items.map((item) => (
            <Highlight key={item.title}>
              <h3>{item.title}</h3>
              {item.text && <p>{item.text}</p>}
            </Highlight>
          ))}
        </Highlights>
      </Container>
    </SandBand>
  );
};
